import React, { useContext, useEffect, useState } from 'react';
import axios from 'axios';
import "../../../assets/css/doctor/doctorpages/HelpSupport.css";
import DoctorMasterLayout from "../doctorlayouts/DoctorMasterLayout";
import { AppContext } from "../../../context/AppContext";

const HelpSupport = () => {
  const { baseUrl, logout } = useContext(AppContext);
  const token = localStorage.getItem("therapist_token");
  const authHeaders = {
    headers: {
      Authorization: `Bearer ${token}`,
      Accept: "application/json",
    },
  };

  const [activeTab, setActiveTab] = useState('faq');
  const [activeFaq, setActiveFaq] = useState(null);

  const [faqs, setFaqs] = useState([]);
  const [quickLinks, setQuickLinks] = useState([]);
  const [loading, setLoading] = useState(true);
  const [loadError, setLoadError] = useState(null);

  const [contactForm, setContactForm] = useState({
    name: '',
    email: '',
    subject: '',
    message: ''
  });
  const [sending, setSending] = useState(false);
  const [contactStatus, setContactStatus] = useState(null); // { type: 'success' | 'error', text }

  // Support hours are fixed policy, not per-doctor data, so they stay static here.
  const supportHours = [
    { day: 'Monday - Friday', time: '9:00 AM - 6:00 PM' },
    { day: 'Saturday', time: '10:00 AM - 4:00 PM' },
    { day: 'Sunday', time: 'Closed' },
  ];

  useEffect(() => {
    if (!token) {
      setLoading(false);
      return;
    }
    const fetchHelpData = async () => {
      try {
        const res = await axios.get(`${baseUrl}/doctor/Help/index`, authHeaders);
        const data = res.data?.data ?? {};
        setFaqs(data.faqs || []);
        setQuickLinks(data.resources || []);
      } catch (err) {
        console.log('Help & Support fetch error:', err);
        if (err?.response?.status === 401) {
          logout();
        } else {
          setLoadError('Failed to load help content.');
        }
      } finally {
        setLoading(false);
      }
    };
    fetchHelpData();
  }, [baseUrl]);

  const handleInputChange = (e) => {
    const { name, value } = e.target;
    setContactForm(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    setSending(true);
    setContactStatus(null);
    try {
      const res = await axios.post(`${baseUrl}/doctor/Help/contact`, contactForm, authHeaders);
      setContactStatus({
        type: 'success',
        text: res.data?.message || 'Your message has been sent successfully! We will get back to you soon.',
      });
      setContactForm({ name: '', email: '', subject: '', message: '' });
    } catch (err) {
      console.log('Contact form submit error:', err);
      if (err?.response?.status === 401) {
        logout();
      } else {
        setContactStatus({
          type: 'error',
          text: err?.response?.data?.message || 'Could not send your message. Please try again.',
        });
      }
    } finally {
      setSending(false);
    }
  };

  return (
    <DoctorMasterLayout>
    <div className="help-container">
      <div className="help-header">
        <h1 className="help-header-title">Help & Support</h1>
        <p className="help-header-text">We're here to help you with any questions or concerns</p>
      </div>

      <div className="tabs-container">
        <button 
          className={activeTab === 'faq' ? 'tab-btn-active' : 'tab-btn'}
          onClick={() => setActiveTab('faq')}
        >
          FAQs
        </button>
        <button 
          className={activeTab === 'contact' ? 'tab-btn-active' : 'tab-btn'}
          onClick={() => setActiveTab('contact')}
        >
          Contact Us
        </button>
        <button 
          className={activeTab === 'resources' ? 'tab-btn-active' : 'tab-btn'}
          onClick={() => setActiveTab('resources')}
        >
          Resources
        </button>
      </div>

      <div className="content-area">
        {loadError && <p className="help-error">{loadError}</p>}

        {activeTab === 'faq' && (
          <div>
            <h2 className="section-title">Frequently Asked Questions</h2>
            {loading ? (
              <p>Loading FAQs…</p>
            ) : faqs.length === 0 ? (
              <p>No FAQs available right now.</p>
            ) : (
              <div className="faq-list">
                {faqs.map(faq => (
                  <div key={faq.id} className="faq-item">
                    <button 
                      className={activeFaq === faq.id ? 'faq-question-active' : 'faq-question'}
                      onClick={() => setActiveFaq(activeFaq === faq.id ? null : faq.id)}
                    >
                      <span>{faq.question}</span>
                      <span className="faq-icon">{activeFaq === faq.id ? '−' : '+'}</span>
                    </button>
                    {activeFaq === faq.id && (
                      <div className="faq-answer">
                        {faq.answer}
                      </div>
                    )}
                  </div>
                ))}
              </div>
            )}
          </div>
        )}

        {activeTab === 'contact' && (
          <div>
            <h2 className="section-title">Contact Support</h2>
            <div className="contact-grid">
              <div className="contact-info">
                <div className="info-card">
                  <div className="info-icon">📧</div>
                  <h3 className="info-card-title">Email Support</h3>
                  <p className="info-card-text">support@doctordashboard.com</p>
                  <span className="info-card-subtext">Response within 24 hours</span>
                </div>
                <div className="info-card">
                  <div className="info-icon">📞</div>
                  <h3 className="info-card-title">Phone Support</h3>
                  <p className="info-card-text">+92-XXX-XXXXXXX</p>
                  <span className="info-card-subtext">Mon-Fri, 9AM-6PM</span>
                </div>
                <div className="info-card">
                  <div className="info-icon">💬</div>
                  <h3 className="info-card-title">Live Chat</h3>
                  <p className="info-card-text">Available Now</p>
                  <button className="chat-btn">Start Chat</button>
                </div>
              </div>
              
              <div className="contact-form-wrapper">
                <div className="contact-form">
                  <div className="form-group">
                    <label className="form-label">Your Name</label>
                    <input 
                      className="form-input"
                      type="text" 
                      name="name"
                      value={contactForm.name}
                      onChange={handleInputChange}
                      placeholder="Enter your name"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Email Address</label>
                    <input 
                      className="form-input"
                      type="email" 
                      name="email"
                      value={contactForm.email}
                      onChange={handleInputChange}
                      placeholder="Enter your email"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Subject</label>
                    <input 
                      className="form-input"
                      type="text" 
                      name="subject"
                      value={contactForm.subject}
                      onChange={handleInputChange}
                      placeholder="Enter subject"
                    />
                  </div>
                  <div className="form-group">
                    <label className="form-label">Message</label>
                    <textarea 
                      className="form-textarea"
                      name="message"
                      value={contactForm.message}
                      onChange={handleInputChange}
                      placeholder="Describe your issue or question"
                      rows="5"
                    ></textarea>
                  </div>

                  {contactStatus && (
                    <p style={{ color: contactStatus.type === 'error' ? 'red' : 'green' }}>
                      {contactStatus.text}
                    </p>
                  )}

                  <button onClick={handleSubmit} className="submit-btn" disabled={sending}>
                    {sending ? 'Sending…' : 'Send Message'}
                  </button>
                </div>
              </div>
            </div>
          </div>
        )}

        {activeTab === 'resources' && (
          <div>
            <h2 className="section-title">Helpful Resources</h2>
            {loading ? (
              <p>Loading resources…</p>
            ) : (
              <div className="resources-grid">
                {quickLinks.map(link => (
                  <div key={link.id} className="resource-card">
                    <div className="resource-icon">{link.icon}</div>
                    <h3 className="resource-card-title">{link.title}</h3>
                    {link.url ? (
                      <a href={link.url} target="_blank" rel="noopener noreferrer" className="resource-btn">
                        View
                      </a>
                    ) : (
                      <button className="resource-btn" disabled>View</button>
                    )}
                  </div>
                ))}
              </div>
            )}
            
            <div className="support-hours">
              <h3 className="support-hours-title">Support Hours</h3>
              <div className="hours-grid">
                {supportHours.map((slot) => (
                  <div className="hours-item" key={slot.day}>
                    <span className="hours-day">{slot.day}</span>
                    <span className="hours-time">{slot.time}</span>
                  </div>
                ))}
              </div>
            </div>
          </div>
        )}
      </div>
    </div>
    </DoctorMasterLayout>
  );
};

export default HelpSupport;